import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { 
  ApiResponse, 
  LoginRequest, 
  LoginResponse, 
  AdminRegistrationRequest,
  User, 
  Report, 
  Department, 
  Category,
  Assignment,
  DashboardStats,
  CreateUserRequest,
  UpdateUserRequest,
  CreateReportRequest,
  UpdateReportRequest,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateAssignmentRequest,
  UpdateAssignmentRequest
} from '../types';
import { API_BASE_URL } from '../config/apiConfig';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for authentication
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          const isLoginAttempt = requestUrl.includes('/auth/login');

          // Token expired or invalid (avoid redirect loop on login attempts)
          if (!isLoginAttempt) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  private async request<T>(config: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.request(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseMessage =
          (error.response?.data as any)?.error?.message ||
          (error.response?.data as any)?.message;

        return {
          success: false,
          error: {
            message: responseMessage || error.message,
            statusCode: error.response?.status || 500,
          },
        };
      }
      return {
        success: false,
        error: {
          message: 'An unexpected error occurred',
          statusCode: 500,
        },
      };
    }
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return this.request({
      method: 'POST',
      url: '/auth/login',
      data: credentials,
    });
  }

  async registerAdmin(payload: AdminRegistrationRequest): Promise<ApiResponse<LoginResponse>> {
    return this.request({
      method: 'POST',
      url: '/admin/register',
      data: payload,
    });
  }

  async uploadMunicipalityLogo(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('logo', file);

    return this.request({
      method: 'POST',
      url: '/admin/upload-logo',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    return this.request({
      method: 'POST',
      url: '/auth/refresh',
    });
  }

  async logout(): Promise<ApiResponse<void>> {
    return this.request({
      method: 'POST',
      url: '/auth/logout',
    });
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request({ method: 'GET', url: '/auth/me' });
  }

  // User Management
  async getUsers(): Promise<ApiResponse<User[]>> {
    return this.request({
      method: 'GET',
      url: '/users',
    });
  }

  async getUser(id: string): Promise<ApiResponse<User>> {
    return this.request({
      method: 'GET',
      url: `/users/${id}`,
    });
  }

  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    return this.request({
      method: 'POST',
      url: '/users',
      data: userData,
    });
  }

  async updateUser(id: string, userData: UpdateUserRequest): Promise<ApiResponse<User>> {
    return this.request({
      method: 'PUT',
      url: `/users/${id}`,
      data: userData,
    });
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/users/${id}`,
    });
  }

  async toggleUserActive(id: string, isActive: boolean): Promise<ApiResponse<User>> {
    return this.request({
      method: 'PUT',
      url: `/users/${id}`,
      data: { isActive },
    });
  }

  // Report Management
  async getReports(): Promise<ApiResponse<Report[]>> {
    return this.request({
      method: 'GET',
      url: '/reports',
    });
  }

  async getReport(id: string): Promise<ApiResponse<Report>> {
    return this.request({
      method: 'GET',
      url: `/reports/${id}`,
    });
  }

  async getReportTimeline(id: string): Promise<ApiResponse<any[]>> {
    return this.request({
      method: 'GET',
      url: `/reports/${id}/timeline`,
    });
  }

  async createReport(reportData: CreateReportRequest): Promise<ApiResponse<Report>> {
    return this.request({
      method: 'POST',
      url: '/reports',
      data: reportData,
    });
  }

  async updateReport(id: string, reportData: UpdateReportRequest): Promise<ApiResponse<Report>> {
    return this.request({
      method: 'PUT',
      url: `/reports/${id}`,
      data: reportData,
    });
  }

  async deleteReport(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/reports/${id}`,
    });
  }

  async updateReportStatus(id: string, status: string): Promise<ApiResponse<Report>> {
    return this.request({
      method: 'PATCH',
      url: `/reports/${id}/status`,
      data: { status },
    });
  }

  // Department Management
  async getDepartments(): Promise<ApiResponse<Department[]>> {
    return this.request({
      method: 'GET',
      url: '/departments',
    });
  }

  async getDepartment(id: string): Promise<ApiResponse<Department>> {
    return this.request({
      method: 'GET',
      url: `/departments/${id}`,
    });
  }

  async createDepartment(departmentData: CreateDepartmentRequest): Promise<ApiResponse<Department>> {
    return this.request({
      method: 'POST',
      url: '/departments',
      data: departmentData,
    });
  }

  async updateDepartment(id: string, departmentData: UpdateDepartmentRequest): Promise<ApiResponse<Department>> {
    return this.request({
      method: 'PUT',
      url: `/departments/${id}`,
      data: departmentData,
    });
  }

  async deleteDepartment(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/departments/${id}`,
    });
  }

  // Category Management
  async getCategories(): Promise<ApiResponse<Category[]>> {
    return this.request({
      method: 'GET',
      url: '/categories',
    });
  }

  async getCategory(id: string): Promise<ApiResponse<Category>> {
    return this.request({
      method: 'GET',
      url: `/categories/${id}`,
    });
  }

  async createCategory(categoryData: CreateCategoryRequest): Promise<ApiResponse<Category>> {
    return this.request({
      method: 'POST',
      url: '/categories',
      data: categoryData,
    });
  }

  async updateCategory(id: string, categoryData: UpdateCategoryRequest): Promise<ApiResponse<Category>> {
    return this.request({
      method: 'PUT',
      url: `/categories/${id}`,
      data: categoryData,
    });
  }

  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/categories/${id}`,
    });
  }

  // Assignment Management
  async getAssignments(params?: {
    status?: string;
    priority?: string;
    assigned_to_id?: string;
    department_id?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Assignment[]>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const url = `/assignments${queryString ? `?${queryString}` : ''}`;
    return this.get<Assignment[]>(url);
    return this.request({
      method: 'GET',
      url: '/assignments',
    });
  }

  async getAssignment(id: string): Promise<ApiResponse<Assignment>> {
    return this.request({
      method: 'GET',
      url: `/assignments/${id}`,
    });
  }

  async createAssignment(assignmentData: CreateAssignmentRequest): Promise<ApiResponse<Assignment>> {
    return this.request({
      method: 'POST',
      url: '/assignments',
      data: assignmentData,
    });
  }

  async updateAssignment(id: string, assignmentData: UpdateAssignmentRequest): Promise<ApiResponse<Assignment>> {
    return this.request({
      method: 'PUT',
      url: `/assignments/${id}`,
      data: assignmentData,
    });
  }

  async deleteAssignment(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/assignments/${id}`,
    });
  }

  // Dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return this.request({
      method: 'GET',
      url: '/dashboard/stats',
    });
  }

  // File Upload
  async uploadPhoto(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('photo', file);

    return this.request({
      method: 'POST',
      url: '/upload/photo',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Health Check
  async healthCheck(): Promise<ApiResponse<any>> {
    return this.request({
      method: 'GET',
      url: '/health',
    });
  }

  // Generic HTTP methods for custom endpoints
  async get<T = any>(url: string): Promise<ApiResponse<T>> {
    return this.request({
      method: 'GET',
      url,
    });
  }

  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request({
      method: 'POST',
      url,
      data,
    });
  }

  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request({
      method: 'PUT',
      url,
      data,
    });
  }

  async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    return this.request({
      method: 'DELETE',
      url,
    });
  }

  async patch<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request({
      method: 'PATCH',
      url,
      data,
    });
  }
}

export const apiService = new ApiService();
export default apiService;
